import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface CookieConsentProps {
  onAccept: () => void;
  onReject: () => void;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept, onReject }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consentStatus = localStorage.getItem('cookieConsent');
    
    // Check if consent needs renewal (6 months)
    const needsRenewal = () => {
      const consentDate = localStorage.getItem('cookieConsentDate');
      if (!consentDate) return true;
      
      const sixMonthsInMs = 6 * 30 * 24 * 60 * 60 * 1000;
      const lastConsentDate = new Date(consentDate).getTime();
      const currentDate = new Date().getTime();
      
      return (currentDate - lastConsentDate) > sixMonthsInMs;
    };

    // Show banner if no consent decision or if consent needs renewal
    if (consentStatus === null || needsRenewal()) {
      // Small delay to ensure proper animation
      setTimeout(() => {
        setIsVisible(true);
      }, 300);
    }
  }, []);

  const handleAccept = () => {
    setIsAnimating(true);
    setTimeout(() => {
      localStorage.setItem('cookieConsent', 'accepted');
      localStorage.setItem('cookieConsentDate', new Date().toISOString());
      setIsVisible(false);
      setIsAnimating(false);
      onAccept();
    }, 300);
  };

  const handleReject = () => {
    setIsAnimating(true);
    setTimeout(() => {
      localStorage.setItem('cookieConsent', 'rejected');
      localStorage.setItem('cookieConsentDate', new Date().toISOString());
      setIsVisible(false);
      setIsAnimating(false);
      onReject();
    }, 300);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 bg-white shadow-xl border-t border-orange-100 z-[9999] transform transition-transform duration-300 ease-in-out ${isAnimating ? 'translate-y-full' : 'translate-y-0'} animate-fadeIn`}
      style={{ animationDuration: '0.3s', boxShadow: '0 -5px 15px -5px rgba(0, 0, 0, 0.1)' }}
    >
      <div className="max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 flex flex-col sm:flex-row items-center justify-between">
        <div className="mb-4 sm:mb-0 sm:mr-8 max-w-2xl">
          <h3 className="text-gray-800 font-medium mb-2 text-lg">Cookie Preferences</h3>
          <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
            We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
            We only collect anonymous analytics data through Google Analytics 4.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link 
            to="/privacy" 
            className="text-sm text-orange-500 hover:text-orange-600 transition duration-200"
          >
            Privacy Policy
          </Link>
          <button
            onClick={handleReject}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors"
            aria-label="Reject cookies"
          >
            Reject All
          </button>
          <button
            onClick={handleAccept}
            className="px-5 py-2.5 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-colors"
            aria-label="Accept cookies"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}; 